import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { Product } from '@lebusiness/types';

// Fetch all products
export function useProducts(companyId: string) {
  return useQuery({
    queryKey: ['products', companyId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('company_id', companyId)
        .is('deleted_at', null)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as Product[];
    },
    enabled: !!companyId,
  });
}

// Fetch single product
export function useProduct(id: string) {
  return useQuery({
    queryKey: ['products', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      return data as Product;
    },
    enabled: !!id,
  });
}

// Create product
export function useCreateProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (product: Omit<Product, 'id' | 'createdAt' | 'updatedAt'>) => {
      const { data, error } = await supabase
        .from('products')
        .insert([product])
        .select()
        .single();

      if (error) throw error;
      return data as Product;
    },
    onSuccess: (data) => {
      // Invalidate and refetch products list
      queryClient.invalidateQueries({ queryKey: ['products', data.companyId] });
    },
  });
}

// Update product
export function useUpdateProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...product }: Partial<Product> & { id: string }) => {
      const { data, error } = await supabase
        .from('products')
        .update(product)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data as Product;
    },
    onSuccess: (data) => {
      // Invalidate queries
      queryClient.invalidateQueries({ queryKey: ['products', data.companyId] });
      queryClient.invalidateQueries({ queryKey: ['products', data.id] });
    },
  });
}

// Delete product (soft delete)
export function useDeleteProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await supabase
        .from('products')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data as Product;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['products', data.companyId] });
    },
  });
}

// Generate next product code
export function useNextProductCode(companyId: string, prefix: string = 'PRD') {
  return useQuery({
    queryKey: ['next-product-code', companyId, prefix],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('products')
        .select('code')
        .eq('company_id', companyId)
        .like('code', `${prefix}-%`)
        .order('code', { ascending: false })
        .limit(1);

      if (error) throw error;

      if (!data || data.length === 0) {
        return `${prefix}-001`;
      }

      // Extract number from last code and increment
      const lastCode = data[0].code;
      const match = lastCode.match(/(\d+)$/);
      if (match) {
        const nextNumber = parseInt(match[1]) + 1;
        return `${prefix}-${String(nextNumber).padStart(3, '0')}`;
      }

      return `${prefix}-001`;
    },
    enabled: !!companyId,
  });
}
